import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ADD_TIME_END, ADD_TIME_START } from '../../../../stores/reducer_HistoryData';

const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000;

function timestampToTaipeiWallDate(timestamp) {
    const numTs = Number(timestamp);
    const safeTs = !isNaN(numTs) && numTs > 0 ? numTs : Date.now();
    const utcShifted = new Date(safeTs + TAIPEI_OFFSET_MS);
    return new Date(
        utcShifted.getUTCFullYear(),
        utcShifted.getUTCMonth(),
        utcShifted.getUTCDate(),
        utcShifted.getUTCHours(),
        utcShifted.getUTCMinutes(),
        utcShifted.getUTCSeconds(),
        utcShifted.getUTCMilliseconds()
    );
}

function taipeiWallDateToTimestamp(dateObj) {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) {
        return Date.now();
    }
    // if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return Date.now();
    return Date.UTC(
        dateObj.getFullYear(),
        dateObj.getMonth(),
        dateObj.getDate(),
        dateObj.getHours(),
        dateObj.getMinutes(),
        dateObj.getSeconds(),
        dateObj.getMilliseconds()
    ) - TAIPEI_OFFSET_MS;
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

function formatTaipeiDateTime(dateObj, includeSeconds = false) {
    if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
    const y = dateObj.getFullYear();
    const m = pad2(dateObj.getMonth() + 1);
    const d = pad2(dateObj.getDate());
    const h = pad2(dateObj.getHours());
    const mi = pad2(dateObj.getMinutes());
    const s = pad2(dateObj.getSeconds());
    return includeSeconds
        ? `${y}-${m}-${d} ${h}:${mi}:${s}`
        : `${y}-${m}-${d} ${h}:${mi}`;
}

const TimeSelector = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const store_start = useSelector(state => state.store_historyData.timestampBegin);
    const store_end = useSelector(state => state.store_historyData.timestampEnd);

    const [togg, setTogg] = useState('start');
    const [startTime, setStartTime] = useState(() => timestampToTaipeiWallDate(store_start));
    const [endTime, setEndTime] = useState(() => timestampToTaipeiWallDate(store_end));

    // ✅ 修正：用 ref 記錄上一次的 store 值，只在「外部變更」時才同步進來
    // 避免自己 dispatch → 自己收到 → 再 dispatch 的無限循環
    const prevStoreStart = useRef(store_start);
    const prevStoreEnd = useRef(store_end);

    useEffect(() => {
        // 只有在 store 值「真的從外部被改變」時，才同步更新本地 state
        if (store_start !== prevStoreStart.current) {
            prevStoreStart.current = store_start;
            setStartTime(timestampToTaipeiWallDate(store_start));
        }
    }, [store_start]);

    useEffect(() => {
        if (store_end !== prevStoreEnd.current) {
            prevStoreEnd.current = store_end;
            setEndTime(timestampToTaipeiWallDate(store_end));
        }
    }, [store_end]);

    // ✅ 修正：移除原本「startTime/endTime 變化就寫 store」的 Effect
    // 改成只在按下確認時才寫入 Redux，徹底切斷雙向循環

    function handleConfirm() {
        const startTs = taipeiWallDateToTimestamp(startTime);
        const endTs = taipeiWallDateToTimestamp(endTime);

        // 更新 ref，避免上面的 store 同步 effect 誤判為外部變更
        prevStoreStart.current = startTs;
        prevStoreEnd.current = endTs;

        dispatch(ADD_TIME_START(startTs));
        dispatch(ADD_TIME_END(endTs));

        let _st = formatTaipeiDateTime(startTime, false);
        let _et = formatTaipeiDateTime(endTime, false);
        if (!_st || !_et) {
            const fallbackDate = new Date();
            if (!_st) _st = formatTaipeiDateTime(fallbackDate, false);
            if (!_et) _et = formatTaipeiDateTime(fallbackDate, false);
        }
        props.setTimeInterval([_st, _et]);
        props.toggleReset(_st, _et);
        if (props.hide) props.hide();
    }

    return (
        <div className='w-100 h-100 pos-a t-0 l-0 z-1 fx-ccc' onClick={props.hide}>
            <div
                className='timeSelctor br-20 w-50 h-40 fx-d-c addSwitch add inputBox blue_2'
                onClick={e => e.stopPropagation()}
            >
                <div className='fx-d-r w-100 h-20 fx-ccc SwitchTag font-ml'>
                    <div
                        className={`fx-ccc outer_active bold_border blue_2 l ${togg === 'start' ? '' : 'dismiss'}`}
                        onClick={() => setTogg('start')}
                    >
                        <div className={`fx-ccc l bold_border ${togg === 'start' ? 'inner_active' : ''}`}>
                            {t('HISTORY_RECORD.MOBILITY_START')} &nbsp;&nbsp; {formatTaipeiDateTime(startTime, false)}
                        </div>
                    </div>

                    <div
                        className={`fx-ccc outer_active blue_2 bold_border r add ${togg === 'end' ? '' : 'dismiss'}`}
                        onClick={() => setTogg('end')}
                    >
                        <div className={`fx-ccc r bold_border ${togg === 'end' ? 'inner_active' : ''}`}>
                            {t('HISTORY_RECORD.MOBILITY_END')} &nbsp;&nbsp; {formatTaipeiDateTime(endTime, false)}
                        </div>
                    </div>
                </div>

                {togg === 'start' ? (
                    <TimeSelectorOperator
                        key="start"
                        Time={startTime}
                        setTime={setStartTime}
                        handleConfirm={handleConfirm}
                        togg={togg}
                        hide={props.hide}
                    />
                ) : (
                    <TimeSelectorOperator
                        key="end"
                        Time={endTime}
                        setTime={setEndTime}
                        handleConfirm={handleConfirm}
                        togg={togg}
                        hide={props.hide}
                    />
                )}
            </div>
        </div>
    );
};

export default TimeSelector;

const TimeSelectorOperator = (props) => {
    const { t } = useTranslation();

    const [dateObj, setDateObj] = useState(() => new Date(props.Time));
    const [hour, setHour] = useState(() => props.Time.getHours());
    const [minute, setMinute] = useState(() => props.Time.getMinutes());
    const [press, setPress] = useState([false, '', '']);

    // ✅ 修正：改用 useMemo 或直接從 props.Time 讀值，不依賴 reference 比對
    // 改成比對時間數值，而非物件參考
    const propsTimeMs = props.Time.getTime();

    useEffect(() => {
        const next = new Date(props.Time);
        setDateObj(next);
        setHour(next.getHours());
        setMinute(next.getMinutes());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propsTimeMs, props.togg]);   // ✅ 用 ms 數值比對，避免 reference 不同但值相同時重複觸發

    const syncTime = useCallback((nextDate) => {
        const next = new Date(nextDate);
        setDateObj(next);
        setHour(next.getHours());
        setMinute(next.getMinutes());
        props.setTime(next);
    }, [props.setTime]);

    const handleChange = useCallback((selectedDate) => {
        const next = new Date(props.Time);
        next.setFullYear(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate()
        );
        syncTime(next);
    }, [props.Time, syncTime]);

    const handleHourMin = useCallback((value, id) => {
        let v = parseInt(value, 10);
        if (Number.isNaN(v)) v = 0;
        if (id === 'h') {
            v = Math.max(0, Math.min(23, v));
            const next = new Date(props.Time);
            next.setHours(v);
            syncTime(next);
        } else {
            v = Math.max(0, Math.min(59, v));
            const next = new Date(props.Time);
            next.setMinutes(v);
            syncTime(next);
        }
    }, [props.Time, syncTime]);

    const addMinusTime = useCallback((_a_m, _id) => {
        const next = new Date(props.Time);
        if (_id === 'h') {
            next.setHours(next.getHours() + (_a_m === '+' ? 1 : -1));
        } else {
            next.setMinutes(next.getMinutes() + (_a_m === '+' ? 1 : -1));
        }
        syncTime(next);
    }, [props.Time, syncTime]);

    useEffect(() => {
        if (!press[0]) return;
        const interval = setInterval(() => {
            addMinusTime(press[1], press[2]);
        }, 150);
        return () => clearInterval(interval);
    }, [press, addMinusTime]);

    return (
        <div className='fx-d-r fx-ccc w-100 h-80 font-ml outer blue_2 bold_border'>
            <div className="w-50 fx-d-c fx-ccc">
                <DatePicker
                    selected={dateObj}
                    onChange={handleChange}
                    inline
                    dateFormat="yyyy-MM-dd"
                />
            </div>

            <div className='seperator'></div>

            <div className="w-50 fx-d-c fx-ccc h-80">
                <div className='h-15 fx-ccc'>{t('GLOBAL.SELECT')}</div>

                <div className='h-50 font-ml fx-d-r fx-cspar w-50'>
                    <div className='w-30 fx-d-c fx-cspar h-100'>
                        <div
                            className='tri_box up'
                            onClick={() => addMinusTime('+', 'h')}
                            onMouseDown={() => setPress([true, '+', 'h'])}
                            onMouseUp={() => setPress([false, '', ''])}
                            onMouseLeave={() => setPress([false, '', ''])}
                        >
                            <div className='time_tri up'><div className='one'></div><div className='two'></div></div>
                        </div>
                        <input
                            className='w-100 timer_inputBox'
                            type='text'
                            value={hour}
                            onChange={e => handleHourMin(e.target.value, 'h')}
                        />
                        <div
                            className='tri_box down'
                            onClick={() => addMinusTime('-', 'h')}
                            onMouseDown={() => setPress([true, '-', 'h'])}
                            onMouseUp={() => setPress([false, '', ''])}
                            onMouseLeave={() => setPress([false, '', ''])}
                        >
                            <div className='time_tri down'><div className='one'></div><div className='two'></div></div>
                        </div>
                    </div>

                    <div>:</div>

                    <div className='w-30 fx-d-c fx-cspar h-100'>
                        <div
                            className='tri_box up'
                            onClick={() => addMinusTime('+', 'm')}
                            onMouseDown={() => setPress([true, '+', 'm'])}
                            onMouseUp={() => setPress([false, '', ''])}
                            onMouseLeave={() => setPress([false, '', ''])}
                        >
                            <div className='time_tri up'><div className='one'></div><div className='two'></div></div>
                        </div>
                        <input
                            className='w-100 timer_inputBox'
                            type='text'
                            value={minute}
                            onChange={e => handleHourMin(e.target.value, 'm')}
                        />
                        <div
                            className='tri_box down'
                            onClick={() => addMinusTime('-', 'm')}
                            onMouseDown={() => setPress([true, '-', 'm'])}
                            onMouseUp={() => setPress([false, '', ''])}
                            onMouseLeave={() => setPress([false, '', ''])}
                        >
                            <div className='time_tri down'><div className='one'></div><div className='two'></div></div>
                        </div>
                    </div>
                </div>

                <div className='h-35 fx-ccc w-60'>
                    <div className='time_confirm fx-ccc' onClick={props.handleConfirm}>
                        {t('GLOBAL.CONFIRM')}
                    </div>
                </div>
            </div>
        </div>
    );
};